/**
 * Exponential moving average aligned to a close series.
 *
 * Seed is the SMA of the first fully finite window of `period` bars. After
 * that, `k = 2/(period+1)`. A non-finite bar breaks the chain — later values
 * stay null rather than resuming with a guessed seed (R2b). Leading holes
 * only delay the seed, so a MACD signal can form after warmup nulls.
 */
import { requireAveragePeriod } from './moving-average';

function isFiniteBar(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value);
}

/* eslint-disable functional/no-let, functional/no-loop-statements, functional/immutable-data */

/**
 * First fully finite window, rolling — O(n), same shape as `simpleMovingAverage`.
 * Leading holes only delay the seed; they do not rescan each candidate window.
 */
function seedWindow(
  bars: readonly number[],
  period: number,
): { readonly end: number; readonly sum: number } | null {
  let sum = 0;
  let bad = 0;
  for (let i = 0; i < bars.length; i++) {
    const incoming = bars[i];
    if (isFiniteBar(incoming)) sum += incoming;
    else bad += 1;
    if (i >= period) {
      const outgoing = bars[i - period];
      if (isFiniteBar(outgoing)) sum -= outgoing;
      else bad -= 1;
    }
    if (i >= period - 1 && bad === 0) return { end: i, sum };
  }
  return null;
}

export function exponentialMovingAverage(
  bars: readonly number[],
  period: number,
): readonly (number | null)[] {
  requireAveragePeriod(period);
  const empty = bars.map(() => null);
  const seedAt = seedWindow(bars, period);
  if (seedAt === null) return empty;
  const seed = seedAt.sum / period;
  if (!Number.isFinite(seed)) return empty;
  const k = 2 / (period + 1);
  const values: (number | null)[] = empty.slice();
  values[seedAt.end] = seed;
  let ema = seed;
  let broken = false;
  for (let i = seedAt.end + 1; i < bars.length; i++) {
    const value = bars[i];
    if (broken || !isFiniteBar(value)) {
      broken = true;
      values[i] = null;
      continue;
    }
    /* k is in (0, 1]; both legs finite → ema stays finite. */
    ema = k * value + (1 - k) * ema;
    values[i] = ema;
  }
  return values;
}

/* eslint-enable functional/no-let, functional/no-loop-statements, functional/immutable-data */
